import { Anchor, Button } from '@cluey/cluey-components';
import { isEmpty, isNull, isUndefined } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { api } from '../../../../api';
import { Student } from '../../../../api/types/student';
import CheckTickIcon from '../../../../assets/images/icon-outline-check-tick.svg';
import MenuMeatballsIcon from '../../../../assets/images/icon-outline-menu-meatball.svg';
import { InlineLoader } from '../../../../common/InlineLoader';
import DropdownMenu from '../../../../components/DropdownMenu/DropdownMenu';
import { SkeletonLoader } from '../../../../components/SkeletonLoader';
import { ENROLMENT_STATUS } from '../../../../types/hubEnums';
import { formatError } from '../../../../util/errors';
import { Checkbox } from '../../../../v2/components/ui/checkbox';
import { SUBSCRIPTION_LOCAL_STORAGE_KEYS, SubscriptionLocalStorage } from '../SubscriptionLocalStorage';
import useSubscriptionContext, {
  CANCELLATION_TYPE,
  CONFIRMATION_TYPE,
  SUBSCRIPTION_TYPE,
} from '../useSubscriptionContext';
import { Confirmation } from './Confirmation';
import { Details } from './Details';
import { ESSLRecordHelper, ESSL_RECORDS_ACTIONS } from './ESSLRecordHelper';

interface ClueyPlusProps {
  selectedStudent: Student | null;
}

export const ClueyPlus: React.FC<ClueyPlusProps> = ({ selectedStudent }) => {
  const {
    clueyPlus: {
      isLoading: isLoadingClueyPlus,
      isESSLRecordsLoading: isLoadingESSLRecords,
      canDowngradeSubscription,
      hasMultipleActiveSubscriptions,
      getSubscription,
      showSpecialOfferDialog,
      showCancellationDialog,
      showConfirmationDialog,
      showStatusAsActive,
      confirmation: { confirmationType, show: confirmationShow },
      esslRecords,
      toast: { setShow: setToastShow, setMessage: setToastMessage },
      showDetailsDialog,
    },
    student: { selectedStudentEnrolments },
    enrolments: {
      active: { isLoading: isLoadingActiveEnrolments },
    },
  } = useSubscriptionContext();

  const { data: accountDetails } = api.account.details.useQuery();

  const {
    subscriptionType,
    subscriptionStatus,
    subscriptionPrice,
    subscriptionPaidUntilDate,
    subscriptionDiscountDuration,
    subscriptionDiscountPercentage,
  } = getSubscription() ?? {};

  // Second active subscription if there's  multiple active subscriptions, usually Cluey+ subscription
  const clueyPlusSubscriptionFromTrialConversion = useMemo(() => {
    if (!isLoadingClueyPlus && subscriptionType === SUBSCRIPTION_TYPE.TRIAL && hasMultipleActiveSubscriptions) {
      return getSubscription(SUBSCRIPTION_TYPE.CLUEY_PLUS);
    }
  }, [getSubscription, hasMultipleActiveSubscriptions, isLoadingClueyPlus, subscriptionType]);

  const isLoading = isLoadingActiveEnrolments || isLoadingClueyPlus || isLoadingESSLRecords;

  const isSubscriptionExpired = useMemo(() => {
    return (
      subscriptionType === SUBSCRIPTION_TYPE.TRIAL &&
      subscriptionPaidUntilDate &&
      moment(subscriptionPaidUntilDate).isBefore(moment(), 'day')
    );
  }, [subscriptionPaidUntilDate, subscriptionType]);

  const isSubscriptionPastPUD = useMemo(() => {
    return subscriptionPaidUntilDate && moment(subscriptionPaidUntilDate).isBefore(moment(), 'day');
  }, [subscriptionPaidUntilDate]);

  const isSubscriptionCancelled = useMemo(() => {
    return subscriptionStatus === 'Cancelled';
  }, [subscriptionStatus]);
  const hasDiscount = subscriptionDiscountPercentage && subscriptionDiscountDuration;

  const price = useMemo(() => {
    switch (subscriptionType) {
      case SUBSCRIPTION_TYPE.CLUEY_PLUS:
        return `$${hasDiscount ? '20*' : subscriptionPrice}/month`;
      case SUBSCRIPTION_TYPE.TRIAL:
        return 'Free';
      case SUBSCRIPTION_TYPE.BUNDLED:
      default:
        return 'Free with tutoring';
    }
  }, [subscriptionType, subscriptionPrice, hasDiscount]);

  const subscriptionDescription = useMemo(() => {
    switch (subscriptionType) {
      case SUBSCRIPTION_TYPE.BUNDLED:
        return [
          'Self-paced learning on your schedule',
          'Get feedback fast with personalised AI support',
          'Your always-on study partner for school success',
        ];
      case SUBSCRIPTION_TYPE.CLUEY_PLUS:
        // Separate billing description
        if (selectedStudentEnrolments.length > 0) {
          return [
            'Smarter study that matches what you learn at school',
            'Guided practice with instant feedback',
            'Confidence through every small win',
          ];
        }
        return [
          'Smart study support, available anytime',
          'Expert learning content, aligned to the curriculum',
          'Build confidence with every step',
        ];
      default:
      case SUBSCRIPTION_TYPE.TRIAL:
        return [
          'Instant help when feeling stuck',
          'Make learning less stressful, more rewarding',
          'Build confidence with every step',
        ];
    }
  }, [selectedStudentEnrolments.length, subscriptionType]);

  const subscriptionStatusText = useMemo(() => {
    // TBD-8542: Needs to show subscription status as Active but need to display Resubscription screen (Cancelled and Expired only)
    if (showStatusAsActive) {
      return <p className="font-medium text-green-5">Active</p>;
    }
    if (isSubscriptionExpired) {
      return <p className="font-medium text-blood-orange-5">Expired</p>;
    }
    // Cancelled status will be shown if the subscription is cancelled and PUD is past
    if (isSubscriptionCancelled && isSubscriptionPastPUD) {
      return <p className="font-medium text-grey-4">Cancelled</p>;
    }
    return <p className="font-medium text-green-5">Active</p>;
  }, [showStatusAsActive, isSubscriptionExpired, isSubscriptionCancelled, isSubscriptionPastPUD]);

  const hasActiveEnrolments = useMemo(() => {
    if (!isEmpty(selectedStudentEnrolments) && !isUndefined(selectedStudent)) {
      return selectedStudentEnrolments.some(
        (enrolment) =>
          ![ENROLMENT_STATUS.CANCELLED, ENROLMENT_STATUS.CANCEL_REQUESTED].includes(enrolment.enrolmentStatus)
      );
    }
    return false;
  }, [selectedStudentEnrolments, selectedStudent]);

  const noEnrolmentOrSubscription = useMemo(() => {
    return !hasActiveEnrolments && isNull(getSubscription());
  }, [hasActiveEnrolments, getSubscription]);

  const remainingTrialDays = useMemo(() => {
    return moment(subscriptionPaidUntilDate).diff(moment(), 'days') + 1;
  }, [subscriptionPaidUntilDate]);

  const [agree, setAgree] = useState(false);
  const [termsAndConditionsError, setTermsAndConditionsError] = useState<string | null>(null);
  const isNZ = accountDetails?.country === 'NZ';
  const privacyPolicyLink = isNZ
    ? 'https://clueylearning.co.nz/privacy-policy/'
    : 'https://clueylearning.com.au/privacy-policy/';

  const renderSubscriptionDescription = useCallback(() => {
    return (
      <div className="flex flex-col gap-y-4 pt-5">
        {subscriptionDescription.map((msg) => (
          <div className="flex flex-row gap-x-2" key={msg}>
            <img src={CheckTickIcon} alt="check tick icon" className="h-4 w-4" />
            <p className="text-sm">{msg}</p>
          </div>
        ))}
      </div>
    );
  }, [subscriptionDescription]);

  const subDescriptionForCancelled = useCallback(() => {
    if (isSubscriptionCancelled) {
      if (!isSubscriptionPastPUD) {
        return (
          <>
            <div className="w-1/2">
              <p className="text-sm">Active until</p>
              <p className="font-medium">{moment(subscriptionPaidUntilDate).format('DD MMM YYYY')}</p>
            </div>
            <div className="w-1/2">
              <p className="text-sm">Status</p>
              {subscriptionStatusText}
            </div>
          </>
        );
      } else {
        return (
          <>
            <div className="w-1/2">
              <p className="text-sm">Last active</p>
              <p className="font-medium">{moment(subscriptionPaidUntilDate).format('DD MMM YYYY')}</p>
            </div>
            <div className="w-1/2">
              <p className="text-sm">Status</p>
              {subscriptionStatusText}
            </div>
          </>
        );
      }
    }
  }, [isSubscriptionCancelled, isSubscriptionPastPUD, subscriptionPaidUntilDate, subscriptionStatusText]);

  const renderSubscriptionDetails = useCallback(() => {
    const subDescForCancelled = subDescriptionForCancelled();

    switch (subscriptionType) {
      case SUBSCRIPTION_TYPE.CLUEY_PLUS:
        if (subDescForCancelled) {
          return subDescForCancelled;
        }
        return (
          <>
            <div className="w-1/3">
              <p className="text-sm">Next payment</p>
              <p className="font-medium">{moment(subscriptionPaidUntilDate).format('DD MMM YYYY')}</p>
            </div>
            <div className="w-1/3">
              <p className="text-sm">Price</p>
              <p className="font-medium">{price}</p>
            </div>
            <div className="w-1/3">
              <p className="text-sm">Status</p>
              {subscriptionStatusText}
            </div>
          </>
        );
      case SUBSCRIPTION_TYPE.BUNDLED:
        if (subDescForCancelled) {
          return subDescForCancelled;
        }
        return (
          <>
            {canDowngradeSubscription ? (
              <div className="w-1/2">
                <p className="text-sm">Active until</p>
                <p className="font-medium">{moment(subscriptionPaidUntilDate).format('DD MMM YYYY')}</p>
              </div>
            ) : (
              <div className="w-1/2">
                <p className="text-sm">Price</p>
                <p className="font-medium">{price}</p>
              </div>
            )}
            <div className="w-1/2">
              <p className="text-sm">Status</p>
              {subscriptionStatusText}
            </div>
          </>
        );
      case SUBSCRIPTION_TYPE.TRIAL:
        return (
          <>
            <div className="w-1/3">
              <p className="text-sm">Trial expires</p>
              <p className="font-medium">{moment(subscriptionPaidUntilDate).format('DD MMM YYYY')}</p>
            </div>
            <div className="w-1/3">
              <p className="text-sm">Price</p>
              <p className="font-medium">{price}</p>
            </div>
            <div className="w-1/3">
              <p className="text-sm">Status</p>
              {subscriptionStatusText}
            </div>
          </>
        );
    }
  }, [
    canDowngradeSubscription,
    price,
    subDescriptionForCancelled,
    subscriptionPaidUntilDate,
    subscriptionStatusText,
    subscriptionType,
  ]);

  const studentSubscriptionBeingCreatedList = SubscriptionLocalStorage.get(
    SUBSCRIPTION_LOCAL_STORAGE_KEYS.CLUEY_PLUS_SUBS_BEING_CREATED
  );

  const studentSubscriptionBeingCreated = useMemo(
    () =>
      studentSubscriptionBeingCreatedList.includes(selectedStudent?.studentId) &&
      (!subscriptionType || (subscriptionType === SUBSCRIPTION_TYPE.TRIAL && subscriptionStatus === 'Active')), // if subscriptionType is not falsy, it means the student has a subscription already
    [studentSubscriptionBeingCreatedList, selectedStudent, subscriptionType, subscriptionStatus]
  );

  const discountedSubscriptionBeingCreatedList = SubscriptionLocalStorage.get(
    SUBSCRIPTION_LOCAL_STORAGE_KEYS.CLUEY_PLUS_DISCOUNTED_SUBS_BEING_CREATED
  );
  const discountedSubscriptionBeingCreated = useMemo(
    () => discountedSubscriptionBeingCreatedList.includes(selectedStudent?.studentId),
    [discountedSubscriptionBeingCreatedList, selectedStudent?.studentId]
  );

  const freeTrialsBeingCreatedList = SubscriptionLocalStorage.get(
    SUBSCRIPTION_LOCAL_STORAGE_KEYS.CLUEY_PLUS_FREE_TRIAL_BEING_CREATED
  );
  const freeTrialsBeingCreated = useMemo(
    () => freeTrialsBeingCreatedList.includes(selectedStudent?.studentId),
    [freeTrialsBeingCreatedList, selectedStudent?.studentId]
  );

  useEffect(() => {
    if (studentSubscriptionBeingCreated) {
      if ([SUBSCRIPTION_TYPE.TRIAL, SUBSCRIPTION_TYPE.CLUEY_PLUS].includes(subscriptionType as SUBSCRIPTION_TYPE)) {
        showConfirmationDialog(CONFIRMATION_TYPE.CLUEY_PLUS_SUBSCRIPTION_CREATED);
      }

      if (
        subscriptionType === SUBSCRIPTION_TYPE.BUNDLED &&
        canDowngradeSubscription &&
        confirmationType !== CONFIRMATION_TYPE.CLUEY_PLUS_DOWNGRADE_SUBSCRIPTION
      ) {
        showConfirmationDialog(CONFIRMATION_TYPE.CLUEY_PLUS_SUBSCRIPTION_CREATED);
      }
    }

    if (freeTrialsBeingCreated) {
      showConfirmationDialog(CONFIRMATION_TYPE.CLUEY_PLUS_SUBSCRIPTION_CREATED);
    }

    if (discountedSubscriptionBeingCreated) {
      showConfirmationDialog(CONFIRMATION_TYPE.CLUEY_PLUS_DISCOUNT_APPLIED);
    }
  }, [
    canDowngradeSubscription,
    confirmationType,
    getSubscription,
    selectedStudent,
    showConfirmationDialog,
    studentSubscriptionBeingCreatedList,
    subscriptionType,
    studentSubscriptionBeingCreated,
    esslRecords,
    discountedSubscriptionBeingCreatedList,
    discountedSubscriptionBeingCreated,
    freeTrialsBeingCreatedList,
    freeTrialsBeingCreated,
  ]);

  const { mutate: signUpNewStudent, isLoading: isSigningUpNewStudent } = api.clueyPlus.signUpNewStudent.useMutation();

  const handleStartFreeTrial = () => {
    if (!agree) {
      setTermsAndConditionsError('Agree to Terms and Conditions to continue');
      return;
    }
    const payload = {
      accountId: accountDetails?.customerSfid,
      countryCode: accountDetails?.country,
      students: [
        {
          sfid: selectedStudent?.studentId,
          firstName: selectedStudent?.firstName,
          lastName: selectedStudent?.lastName,
          yearLevel: selectedStudent?.currentYearLevel,
          state: accountDetails?.country === 'NZ' ? '' : selectedStudent?.location,
          postcode: '',
        },
      ],
    };

    signUpNewStudent(payload, {
      onSuccess: () => {
        showConfirmationDialog(CONFIRMATION_TYPE.CLUEY_PLUS_SUBSCRIPTION_CREATED);
        SubscriptionLocalStorage.set(
          SUBSCRIPTION_LOCAL_STORAGE_KEYS.CLUEY_PLUS_FREE_TRIAL_BEING_CREATED,
          selectedStudent?.studentId
        );
      },
      onError: (error) => {
        setToastShow(true);
        setToastMessage(formatError(error));
        SubscriptionLocalStorage.remove(
          SUBSCRIPTION_LOCAL_STORAGE_KEYS.CLUEY_PLUS_FREE_TRIAL_BEING_CREATED,
          selectedStudent?.studentId
        );
      },
    });
  };

  if (isLoading) {
    return (
      <div className="flex w-full flex-col gap-y-5 lg:flex-row lg:gap-x-10">
        <div className="flex w-full flex-col gap-y-5 lg:sticky lg:top-24 lg:w-1/2 lg:self-start">
          <SkeletonLoader loading={isLoading} className="h-4 w-32" />
          <SkeletonLoader loading={isLoading} className="mt-4 h-12 w-full" />
          <SkeletonLoader loading={isLoading} className="mt-3 h-32 w-full" />
        </div>
        <div className="flex w-full flex-col gap-y-5 lg:sticky lg:top-24 lg:w-1/2 lg:self-start">
          <SkeletonLoader loading={isLoading} className="h-12 w-full" />
          <SkeletonLoader loading={isLoading} className="mt-2 h-48 w-full" />
          <SkeletonLoader loading={isLoading} className="mt-1 h-16 w-full" />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex w-full flex-col gap-y-5 lg:sticky lg:top-24 lg:w-1/2 lg:self-start">
        {noEnrolmentOrSubscription ? (
          <>
            <div className="flex flex-row justify-start">
              <h2 className="merriweather text-2xl font-bold">Cluey+</h2>
              <span className="ml-3 flex items-center rounded-full bg-green-3 px-2 py-1 text-center text-xs font-semibold">
                Try for free
              </span>
            </div>
            {renderSubscriptionDescription()}
            {!studentSubscriptionBeingCreated && !freeTrialsBeingCreated && (
              <>
                <div>
                  <div className="mb-1 mt-5 flex flex-row">
                    <div className="h-12 w-12">
                      <Checkbox
                        id="signup-agree"
                        checked={agree}
                        aria-invalid={!!termsAndConditionsError}
                        onCheckedChange={(checked) => {
                          setAgree(checked);
                          setTermsAndConditionsError('');
                        }}
                      />
                    </div>
                    <label htmlFor="signup-agree" className="!font-normal">
                      I have read and agree to Cluey+
                      <Anchor
                        className="ml-1"
                        href={
                          isNZ
                            ? 'https://clueylearning.co.nz/cluey-plus-agreement/'
                            : 'https://clueylearning.com.au/cluey-plus-agreement/'
                        }
                        size="base"
                        target="_blank"
                      >
                        Terms and Conditions
                      </Anchor>
                    </label>
                  </div>
                  {termsAndConditionsError && <div className="-mt-2 text-xs text-red-5">{termsAndConditionsError}</div>}
                </div>
                <div className="mb-1">
                  By continuing, I consent that Cluey collects, uses and discloses my personal information in accordance
                  with Cluey's
                  <Anchor className="ml-[0.25rem]" href={privacyPolicyLink} size="base" target="_blank">
                    Privacy Policy.
                  </Anchor>
                </div>
                <Button
                  disabled={isSigningUpNewStudent}
                  size="lg"
                  className="flex !h-10 w-full flex-row items-center justify-center !border-blood-orange-4 !bg-blood-orange-4 !font-medium text-white"
                  onClick={handleStartFreeTrial}
                >
                  {isSigningUpNewStudent ? <InlineLoader /> : 'Sign up for free trial'}
                </Button>
              </>
            )}
          </>
        ) : (
          <>
            <div className="flex flex-row justify-between">
              <div className="flex flex-row items-center gap-x-2">
                <p className="merriweather text-2xl font-bold">Cluey+</p>
                {subscriptionType === SUBSCRIPTION_TYPE.TRIAL && !isSubscriptionExpired && !isSubscriptionCancelled && (
                  <span className="flex items-center rounded-full bg-green-3 px-2 py-1 text-center text-xs font-semibold">
                    Free trial ends in {remainingTrialDays} {remainingTrialDays === 1 ? 'day' : 'days'}
                  </span>
                )}
                {isSubscriptionExpired && (
                  <span className="flex items-center rounded-full bg-[#E4DED7] px-2 py-1 text-center text-xs font-semibold">
                    Free trial ended
                  </span>
                )}
              </div>
              {((subscriptionType === SUBSCRIPTION_TYPE.CLUEY_PLUS && !isSubscriptionCancelled) ||
                clueyPlusSubscriptionFromTrialConversion) &&
                !studentSubscriptionBeingCreated && (
                  <DropdownMenu
                    items={[
                      {
                        label: 'End Cluey+ subscription',
                        onClick: () => {
                          if (
                            ESSLRecordHelper.hasRecordAction(
                              esslRecords,
                              ESSL_RECORDS_ACTIONS.CLUEY_PLUS_APPLY_DISCOUNT
                            ) ||
                            clueyPlusSubscriptionFromTrialConversion ||
                            selectedStudentEnrolments.length > 0
                          ) {
                            showCancellationDialog(CANCELLATION_TYPE.CANCEL_CLUEY_PLUS);
                          } else {
                            showSpecialOfferDialog();
                          }
                        },
                        disabled: hasActiveEnrolments,
                        optionLabel: hasActiveEnrolments
                          ? 'Cancel your active tutoring enrolments first before ending Cluey+ subscription'
                          : undefined,
                      },
                    ]}
                    icon={<img src={MenuMeatballsIcon} alt="menu icon" />}
                    showIcon={false}
                    buttonClassName="border-none !px-1 !h-5"
                    itemClassName="!py-3 !px-5 rounded-lg"
                    dropdownClassName="!min-w-[300px]"
                  />
                )}
            </div>
            <div className="flex flex-row gap-x-2">{renderSubscriptionDetails()}</div>
            {!isSubscriptionCancelled && subscriptionDiscountPercentage && subscriptionDiscountDuration && (
              <div className="!text-sm">
                <p>
                  *33% discount for 3 months applied. Monthly payments will revert to $30/month from{' '}
                  {moment(subscriptionPaidUntilDate).add(3, 'months').format('DD MMM')}.
                </p>
              </div>
            )}
            {renderSubscriptionDescription()}
          </>
        )}
      </div>
      <div className="w-full lg:w-1/2">
        {selectedStudent && !noEnrolmentOrSubscription && <Details student={selectedStudent} />}
        {noEnrolmentOrSubscription && (confirmationShow || studentSubscriptionBeingCreated) && (
          <Confirmation
            confirmationType={confirmationType ?? CONFIRMATION_TYPE.CLUEY_PLUS_SUBSCRIPTION_CREATED}
            onClose={() => {
              showDetailsDialog();
            }}
          />
        )}
      </div>
    </>
  );
};
